import { HttpClient, HttpContext, HttpHeaders } from '@angular/common/http';
import { AfterViewInit, Component, inject, OnInit } from '@angular/core';
import { isJsonObject } from '@se/cp.common';
import { firstValueFrom, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { HTTP_TOKEN_IGNORE_ERRORS, ServerRootService } from '../../../../utils/routing/serverroot.service';
import { EExternalIntegrationCallType, EHttpMethod, Json } from '../../tp-integrations.interface';
import { AbstractCallComponent } from '../abstract-call/abstract-call.component';
import { jsonToSoap, soapToJson } from './helpers';

@Component({
    selector: 'se-rest-call',
    templateUrl: './rest-call.component.html',
    styleUrls: ['../call-style/call.scss'],
    standalone: true,
})
export class RestCallComponent extends AbstractCallComponent implements OnInit, AfterViewInit {
    private readonly http = inject(HttpClient);
    private readonly serverRootService = inject(ServerRootService);

    url: string | null = null;
    body: Json | null = null;

    ngOnInit() {
        super.ngOnInit();
        this.parsePropsWithData();
        this.callInit();
    }

    getEstensaBaseUrl(): string {
        return this.serverRootService.getBaseRoot();
    }

    getEstensaToken(): string {
        return localStorage.getItem('access_token') || '';
    }

    parsePropsWithData = () => {
        let url: string = '';
        if (this.dataStore) {
            let parsedStrings = this.parseAllSourceWithDataStore(this.call);
            parsedStrings = this.applyPostParseProcessing({ ...this.call, ...parsedStrings });

            url = parsedStrings.url ?? '';
            this.body = parsedStrings.body ?? null;
        } else {
            const parsedCall = this.applyPostParseProcessing({ ...this.call });
            url = parsedCall.url ?? '';
            this.body =
                parsedCall.body && typeof parsedCall.body === 'string' ? JSON.parse(parsedCall.body) : parsedCall.body;
        }

        if (this.call.type === EExternalIntegrationCallType.REST_ESTENSA && !this.call.test) {
            url = `${this.getEstensaBaseUrl()}${url}`;
        }

        this.url = url;
    };

    private getRestErrorMessage = (err: any): string => {
        return err.error?.exceptionMessage ?? err.message ?? this.call.errorHandling?.message ?? 'GenericRestError';
    };

    createHeaders = (): HttpHeaders => {
        let parsedData = this.parseAllSourceWithDataStore(this.call);
        parsedData = this.applyPostParseProcessing({ ...this.call, ...parsedData });

        const headersObj: Record<string, string> = parsedData.headers ?? {};
        const authData = parsedData.authenticationData;
        if (authData) {
            const parsedAuthData = parsedData.authenticationData;
            headersObj['Authentication'] = `credentials ${btoa(JSON.stringify(parsedAuthData))}`;
        }
        switch (this.call.type) {
            case EExternalIntegrationCallType.REST_ESTENSA:
                headersObj['Content-Type'] = 'application/json';
                headersObj['Accept'] = 'application/json,text/plain,*/*';
                break;
            case EExternalIntegrationCallType.REST_EXTERNAL:
                headersObj['Content-Type'] = 'application/json';
                break;
            case EExternalIntegrationCallType.REST_EXTERNAL_SOAP:
                headersObj['Content-Type'] = 'application/soap+xml';
                break;
        }
        return new HttpHeaders(headersObj);
    };

    createOptions = (): Record<string, any> => {
        const isEstensa = this.call.type === EExternalIntegrationCallType.REST_ESTENSA;
        const isSoap = this.call.type === EExternalIntegrationCallType.REST_EXTERNAL_SOAP;
        const headers = this.createHeaders();
        const options: Record<string, any> = { headers };
        if (!isEstensa) {
            const context = new HttpContext();
            context.set(HTTP_TOKEN_IGNORE_ERRORS, true);
            options.context = context;
        }
        if (isSoap) {
            options.responseType = 'text';
        }

        return options;
    };

    httpGet = async () => {
        if (!this.url) {
            return this.end();
        }

        const otherOptions = this.createOptions();

        return firstValueFrom(
            this.http.get(this.url, otherOptions).pipe(
                map((data) =>
                    this.call.type === EExternalIntegrationCallType.REST_EXTERNAL_SOAP
                        ? soapToJson(data as string)
                        : data,
                ),
                catchError((err) => {
                    this.callThrowError.emit(this.getRestErrorMessage(err));
                    return throwError(() => err);
                }),
            ),
        );
    };

    httpPost = async () => {
        if (!this.url) {
            return this.end();
        }

        const otherOptions = this.createOptions();
        const body =
            this.call.type === EExternalIntegrationCallType.REST_EXTERNAL_SOAP && isJsonObject(this.body)
                ? jsonToSoap(this.body, this.call.xmlns)
                : this.body;
        return firstValueFrom(
            this.http.post(this.url, body, otherOptions).pipe(
                map((data) =>
                    this.call.type === EExternalIntegrationCallType.REST_EXTERNAL_SOAP
                        ? soapToJson(data as string)
                        : data,
                ),
                catchError((err) => {
                    this.callThrowError.emit(this.getRestErrorMessage(err));
                    return throwError(() => ({ ...err, status: 500 }));
                }),
            ),
        );
    };

    ngAfterViewInit(): void {
        if (this.url) this.showLoading.set(true);
    }

    protected async run(): Promise<any> {
        switch (this.call.httpMethod) {
            case EHttpMethod.POST:
                return this.httpPost();
            case EHttpMethod.GET:
            default:
                return this.httpGet();
        }
    }

    protected end() {
        if (this.errorValueHandler(this.getValueStoreDataFromPath())) {
            this.callThrowError.emit(this.call.errorHandling?.message ?? 'GenericRestError');
        } else {
            super.end();
        }
    }
}
